<script lang="tsx" setup>
import { KirbyImageData } from "@/queries/image"
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue"
import { useZoom } from "@/composables/use-zoom"
import SVG_ZoomIn from "@/components/SVGs/SVG_ZoomIn.vue"

const props = defineProps<{
  data: KirbyImageData
  isActive: boolean
}>()

const emit = defineEmits<{
  load: []
  heightChange: [height: number]
}>()

const { openZoom } = useZoom()

const image = ref<HTMLImageElement>()
const container = ref<HTMLDivElement>()

const onResize = () => {
  if (!container.value || !image.value) return
  const iw = image.value.naturalWidth
  const ih = image.value.naturalHeight
  if (!iw || !ih) return

  // Use the decoded image's true aspect (auto-oriented by Kirby's resize) rather
  // than Kirby's file.ratio, which ignores EXIF orientation and is wrong for
  // iPhone/iPad photos. Box must match the bitmap or object-fit: fill squashes it.
  const ratio = iw / ih
  const cw = container.value.clientWidth
  const ch = container.value.clientHeight

  let renderedHeight: number
  if (cw / iw < ch / ih) {
    renderedHeight = cw / ratio
    image.value.style.width = `${cw}px`
    image.value.style.height = `${renderedHeight}px`
  } else {
    renderedHeight = ch
    image.value.style.width = `${ch * ratio}px`
    image.value.style.height = `${renderedHeight}px`
  }

  // Report the fitted height so the gallery can collapse to the active image.
  // Measured against the fixed slideshow box, so this is stable per image.
  if (props.isActive && renderedHeight > 0) {
    emit("heightChange", renderedHeight)
  }
}

watch(
  () => props.isActive,
  async value => {
    if (value) {
      await nextTick()
      onResize()
    }
  },
)

const onLoad = async () => {
  if (image.value) {
    onResize()
    await nextTick()
    image.value.classList.remove("opacity-0")
  }
  emit("load")
}

onMounted(() => {
  if (image.value) {
    image.value.onload = onLoad
    // Cached images are already complete before the handler is attached, so
    // onload never fires — run it directly so the reveal/load signal still emits.
    if (image.value.complete && image.value.naturalWidth > 0) {
      onLoad()
    }
  }
  window.addEventListener("resize", onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize)
})
</script>

<template>
  <div class="relative flex flex-col w-full h-full">
    <div
      ref="container"
      class="relative z-50 flex items-center justify-center w-full h-full"
      :class="{
        'pointer-events-none': !isActive,
      }"
    >
      <button
        type="button"
        data-slide-content="image"
        class="group relative inline-block cursor-zoom-in focus:outline-none"
        tabindex="-1"
        :aria-label="`Zoom image: ${data.alt || 'image'}`"
        @click="() => openZoom(data, image!.getBoundingClientRect(), image!)"
      >
        <img
          ref="image"
          draggable="false"
          :data-zoom-id="data.uuid"
          class="select-none transition-opacity duration-300 border overflow-visible"
          :loading="isActive ? 'eager' : 'lazy'"
          sizes="(min-width: 1024px) calc(100vw - 12rem), 100vw"
          :src="data.mime === 'image/gif' ? data.url : data.src"
          :srcset="data.mime !== 'image/gif' ? data.srcset : undefined"
          :alt="data.alt"
        />
        <div
          class="md:hidden absolute lg:top-3 lg:right-3 top-1 right-1 flex pointer-events-none items-center p-1.5 lg:p-0 justify-center bg-white lg:w-12 lg:h-12 w-12 h-12 border lg:opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <SVG_ZoomIn />
        </div>
      </button>
    </div>
  </div>
</template>
